import type {ImageCrop, LogoConfig, PhotoRequirement} from './types';

export const DEFAULT_CROP: ImageCrop = {scale: 1, offset_x: 0, offset_y: 0};
export const CROP_MIN_SCALE = 1;
export const CROP_MAX_SCALE = 5;

/**
 * Constrain a crop so the image always covers the frame (no empty gaps) and the
 * scale stays in range. The image is laid out to exactly cover the frame at
 * scale 1 (object-fit: cover), so the scaled layout box is frame*scale; to keep
 * it covering the frame the offset can move at most frame*(scale-1)/2 each way.
 */
export function clampCrop(crop: ImageCrop, frameW: number, frameH: number): ImageCrop {
  const scale = Math.min(CROP_MAX_SCALE, Math.max(CROP_MIN_SCALE, crop.scale || 1));
  const maxX = (frameW * (scale - 1)) / 2;
  const maxY = (frameH * (scale - 1)) / 2;
  // `+ 0` normalises -0 (from clamping at scale 1) to 0 for clean equality/serialization.
  return {
    scale,
    offset_x: Math.min(maxX, Math.max(-maxX, crop.offset_x || 0)) + 0,
    offset_y: Math.min(maxY, Math.max(-maxY, crop.offset_y || 0)) + 0,
  };
}

/** CSS transform that applies a crop to an object-fit:cover image. */
export function cropTransform(crop: ImageCrop): string {
  return `translate(${crop.offset_x}px, ${crop.offset_y}px) scale(${crop.scale})`;
}

/** Pixel size of a logo's crop frame: the element box (absolute) or config size (flow). */
export function logoFrameSize(item: PhotoRequirement): {w: number; h: number} {
  const cfg = item.config as LogoConfig;
  const isAbsolute = item.position_mode === 'absolute';
  return {
    w: (isAbsolute ? item.width : cfg.width) ?? 200,
    h: (isAbsolute ? item.height : cfg.height) ?? 150,
  };
}
